import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, Package, ShoppingBag, ArrowRight } from "lucide-react";

const NotificationOverlay = ({ data, close }) => {
  // Check if it's a new product drop or just an order update
  const isNewDrop = data?.type === "new_drop";

  // 🔥 Audio Logic: Jab bhi 'data' change hoga (naya notification aayega), sound play hoga
  useEffect(() => {
    if (data) {
      // Audio path check karna (public folder mein honi chahiye files)
      const audioPath = isNewDrop ? "/drop_alert.mp3" : "/ping.mp3";
      const audio = new Audio(audioPath);
      
      audio.play().catch((e) => {
        console.warn("Autoplay blocked: User needs to interact with the site first.", e);
      });
    }
  }, [data, isNewDrop]);

  if (!data) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        initial={{ opacity: 0, x: 200, scale: 0.8, rotate: 5 }}
        animate={{ opacity: 1, x: 0, scale: 1, rotate: 0 }}
        exit={{ opacity: 0, x: 200, scale: 0.8, rotate: 5 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="fixed top-24 right-6 z-[10000] w-[90%] max-w-[380px] pointer-events-auto"
      >
        <div className={`relative overflow-hidden p-6 rounded-[2.5rem] border backdrop-blur-3xl shadow-[0_50px_100px_rgba(0,0,0,0.9)] transition-all duration-700 ${
          isNewDrop 
          ? "bg-emerald-500/10 border-emerald-500 shadow-emerald-500/20" 
          : "bg-zinc-950/90 border-white/10 shadow-black"
        }`}>
          
          {/* Animated Scanning HUD Line */}
          <motion.div 
            animate={{ top: ["-10%", "110%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className={`absolute left-0 w-full h-[1px] opacity-20 z-0 ${isNewDrop ? 'bg-emerald-500' : 'bg-white'}`}
          />
          
          {/* Neon Top Bar */}
          <div className={`absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-${isNewDrop ? 'emerald' : 'zinc'}-500 to-transparent`} />
          
          {/* Pulse Effect */}
          {isNewDrop && (
            <div className="absolute inset-0 bg-emerald-500/5 animate-pulse pointer-events-none" />
          )}

          <div className="flex gap-5 items-start relative z-10">
            {/* Asset Visual */}
            <div className="flex-shrink-0">
              {data.img ? (
                <div className="relative group">
                   <img 
                    src={data.img} 
                    className="w-20 h-24 object-cover rounded-2xl border-2 border-white/10 group-hover:border-emerald-500 transition-all duration-500 shadow-2xl" 
                    alt="Asset" 
                    crossOrigin="anonymous"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-emerald-500 text-black p-1.5 rounded-lg shadow-lg">
                    <ShoppingBag size={12} strokeWidth={3} />
                  </div>
                </div>
              ) : (
                <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center text-black shadow-2xl ${
                  isNewDrop ? "bg-emerald-500 shadow-emerald-500/40" : "bg-white"
                }`}>
                  {isNewDrop ? <Zap size={28} fill="currentColor" className="animate-pulse" /> : <Package size={28} />}
                </div>
              )}
            </div>
            
            {/* Content Section */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[9px] font-[1000] uppercase tracking-[0.4em] italic ${
                  isNewDrop ? "text-emerald-400" : "text-zinc-500"
                }`}>
                  {isNewDrop ? "// FLASH_DROP_LIVE" : "// COMM_LINK_UP"}
                </span>
                <button 
                  onClick={close} 
                  className="p-1.5 hover:bg-white/10 rounded-xl text-zinc-600 hover:text-white transition-all"
                >
                  <X size={16}/>
                </button>
              </div>

              <h4 className="text-[15px] font-[1000] text-white uppercase italic tracking-tighter leading-tight truncate">
                {data.title || (isNewDrop ? "NEW_ASSET_ONLINE" : "LOG_UPDATE")}
              </h4>
              
              <p className="text-[11px] font-bold text-zinc-400 uppercase mt-2 leading-relaxed tracking-tight italic opacity-80">
                {data.message}
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex gap-3 mt-6 relative z-10">
            <button 
                onClick={() => { 
                  if (isNewDrop && data.productId) {
                      window.location.href = `/product/${data.productId}`;
                  } else if (data.orderId) {
                      window.location.href = `/track/${data.orderId}`;
                  } else {
                      window.location.href = "/profile"; 
                  }
                  close(); 
                }}
                className={`flex-1 py-4 rounded-2xl text-[10px] font-[1000] uppercase transition-all tracking-[0.2em] flex items-center justify-center gap-2 group ${
                isNewDrop 
                ? "bg-emerald-500 text-black hover:bg-white hover:scale-[1.02]" 
                : "bg-white/5 text-white border border-white/10 hover:bg-white hover:text-black hover:scale-[1.02]"
                }`}
            >
                {isNewDrop ? "SECURE_ASSET" : "VIEW_LOGS"}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
            
            {!isNewDrop && (
                <button 
                    onClick={close}
                    className="px-5 py-4 bg-zinc-900 border border-white/5 rounded-2xl text-zinc-500 hover:text-white transition-all"
                >
                    <X size={16} />
                </button>
            )}
          </div>

          {/* Sold out Warning */}
          {isNewDrop && (
            <div className="mt-4 flex items-center justify-center gap-3 overflow-hidden opacity-50">
               <div className="h-[1px] flex-1 bg-emerald-500/30"></div>
               <span className="text-[7px] font-black text-emerald-500 uppercase tracking-widest animate-pulse">
                  LIMITED_STOCKS // NO_RESTOCKS_CONFIRMED
               </span>
               <div className="h-[1px] flex-1 bg-emerald-500/30"></div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationOverlay;